import { DSSAnalyticsEvents } from '@dapperlabs/core-fe';
import { useAnalytics } from '@dapperlabs/react-analytics';
import { sansPrefix } from '@onflow/fcl';
import { useCallback } from 'react';
import { useSession } from 'src/edge/session';
import { extractValue } from 'src/general/utils/helpers';
import { SEGMENT_EVENTS_ID_ENUM } from 'src/lib/segment';

export enum TIMELINE_ITEM_LABELS {
  DISCIPLINE_RED = 'red_card',
  DISCIPLINE_YELLOW = 'yellow_card',
  GOAL = 'goal',
  MOMENT = 'moment',
  SUBSTITUTION = 'change',
}

const MOMENT_DETAIL_EVENTS: DSSAnalyticsEvents = {
  CAROUSEL_ITEM_CLICK:
    SEGMENT_EVENTS_ID_ENUM.MOMENT_DETAILS_CAROUSEL_ITEM_CLICK,
  GAME_HIGHLIGHTS_ITEM_CLICK:
    SEGMENT_EVENTS_ID_ENUM.MOMENT_DETAILS_HIGHLIGHTS_ITEM_CLICK,
  GAME_HIGHLIGHTS_TOOLTIP_VIEW:
    SEGMENT_EVENTS_ID_ENUM.MOMENT_DETAILS_HIGHLIGHTS_TOOLTIP_VIEW,
  HERO_SECTION_DESCRIPTION_CLICK:
    SEGMENT_EVENTS_ID_ENUM.MOMENT_DETAILS_DESCRIPTION_ITEM_CLICK,
  LANGUAGE_MENU_CLICK:
    SEGMENT_EVENTS_ID_ENUM.MOMENT_DETAILS_CAROUSEL_MODAL_ITEM_CLICK,
  LANGUAGE_SETTINGS_CHANGE:
    SEGMENT_EVENTS_ID_ENUM.MOMENT_DETAILS_CAROUSEL_MODAL_QUICK_SETTINGS_SET,
  PLAYER_PERFORMANCE_ITEM_CLICK:
    SEGMENT_EVENTS_ID_ENUM.MOMENT_DETAILS_PLAYER_ITEM_CLICK,
  VIDEO_CLOSE_BUTTON_CLICK:
    SEGMENT_EVENTS_ID_ENUM.MOMENT_DETAILS_CAROUSEL_MODAL_ITEM_CLICK,
  VIDEO_OPEN_FULLSCREEN:
    SEGMENT_EVENTS_ID_ENUM.MOMENT_DETAILS_CAROUSEL_ITEM_CLICK,
  VOLUME_SETTINGS:
    SEGMENT_EVENTS_ID_ENUM.MOMENT_DETAILS_CAROUSEL_MODAL_QUICK_SETTINGS_SET,
};

enum MOMENT_VARIANTS {
  AUTH_LISTED = 'auth_listed',
  AUTH_UNLISTED = 'auth_unlisted',
  EDITION = 'edition',
  OWNED_LISTED = 'owned_listed',
  OWNED_UNLISTED = 'owned_unlisted',
  VISITOR_LISTED = 'visitor_listed',
  VISITOR_UNLISTED = 'visitor_unlisted',
}
const getMomentProps = (moment, userId) => {
  const metadata = moment?.edition?.play?.metadata;

  const getComponentVariant = ({ listing, ownerId }) => {
    if (!ownerId) {
      return MOMENT_VARIANTS.EDITION;
    }
    if (!userId) {
      if (listing) {
        return MOMENT_VARIANTS.VISITOR_LISTED;
      }
      return MOMENT_VARIANTS.VISITOR_UNLISTED;
    }

    if (sansPrefix(ownerId) === sansPrefix(userId)) {
      if (listing) {
        return MOMENT_VARIANTS.OWNED_LISTED;
      }
      return MOMENT_VARIANTS.OWNED_UNLISTED;
    }

    if (listing) {
      return MOMENT_VARIANTS.AUTH_LISTED;
    }
    return MOMENT_VARIANTS.AUTH_UNLISTED;
  };

  return {
    component_variant: getComponentVariant({
      listing: moment?.listing,
      ownerId: moment?.owner_address,
    }),
    edition_id: extractValue(moment?.edition?.id),
    edition_tier: extractValue(moment?.edition?.tier),
    moment_id: Number(extractValue(moment?.id)) || undefined,
    moment_serial_number: extractValue(moment?.serial_number),
    moment_type: extractValue(metadata?.play_type),
    play_id: extractValue(metadata?.play_data_id),
    play_match_away_team: extractValue(metadata?.match_away_team),
    play_match_highlighted_team: extractValue(metadata?.match_highlighted_team),
    play_match_home_team: extractValue(metadata?.match_home_team),
    play_player_country: extractValue(metadata?.player_country),
    play_player_first_name: extractValue(metadata?.player_first_name),
    play_player_last_name: extractValue(metadata?.player_last_name),
    play_season: extractValue(metadata?.match_season),
    play_type: extractValue(metadata?.play_type),
    series_id: extractValue(moment?.edition?.series?.id),
    series_name: extractValue(moment?.edition?.series?.name),
    set_id: extractValue(moment?.edition?.set?.id),
    set_name: extractValue(moment?.edition?.set?.name),
  };
};

export const useMomentAnalytics = (moment = null) => {
  const { track } = useAnalytics();
  const { state } = useSession();
  // @ts-ignore
  const userId = state?.context?.profile?.flowAddress ?? null;

  const trackHandler = useCallback(
    (trackId, props) => {
      if (!trackId) {
        return;
      }

      const trackingProps = { ...props, ...getMomentProps(moment, userId) };
      if (trackId === MOMENT_DETAIL_EVENTS.GAME_HIGHLIGHTS_TOOLTIP_VIEW) {
        trackingProps.item_label = TIMELINE_ITEM_LABELS[props.item_label];
      }
      track(trackId, trackingProps);
    },
    [track, moment, state, userId],
  );

  return {
    events: MOMENT_DETAIL_EVENTS,
    trackHandler,
  };
};
